import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, StyleSheet, TouchableOpacity } from 'react-native';

import { PickInfoFragment } from '@/api/entries/query.generated';
import { EntryDetails } from '@/api/entries/types/types';
import DownIcon from '@/assets/icons/downSmall';
import { EntryWarning } from '@/components/EntryWarning';
import { LineSeparator } from '@/components/LineSeparator';
import { Text } from '@/components/TextComponent';
import { EntryStatus, EntryStatusBadge } from '@/components/entry-card';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { getEntryDetails, getMultiplier, getPayoutLabel } from '@/hooks/use-entries-utils';
import { common } from '@/styles/styles';
import { GameMode, Result } from '@/types/api.generated';
import { getEntryPayout } from '@/utils/getEntryPayout';
import { toLocaleCurrency } from '@/utils/numeric/currency';

import {
    getEntryMultiplier,
    getEntryResultAmount,
    getEntryScreenStatusBadge,
    getEntryTitle,
    isEntryWin,
    isFreeEntry,
} from '../entries-utils';
import { usePickemCashedOutEntries } from '../hooks/use-pickem-cashed-out-entries';

type EntryHeaderProps = {
    entry?: EntryDetails;
    isLoading: boolean;
    entryPayout: ReturnType<typeof getEntryPayout>;
    picks?: PickInfoFragment[];
    openPayoutSheet?: () => void;
};

export const EntryHeader = memo(({ entry, isLoading, entryPayout, openPayoutSheet = () => {} }: EntryHeaderProps) => {
    const { t } = useTranslation(['common', 'bets']);

    const picks = useMemo(() => entry?.picks ?? [], [entry?.picks]);
    const { voidedCount, multiplierType, hasRevivedPicks, hasRescuedPicks } = getEntryDetails(entry, entryPayout);
    const isRefunded = entry?.result === Result.Void;
    const cachedCashedOutEntry = usePickemCashedOutEntries(s => s.entries.get(entry?.id ?? '')) ?? null;
    const isWinning = isEntryWin(entry) && !cachedCashedOutEntry;
    const entryStatus = cachedCashedOutEntry
        ? EntryStatus.CashedOut
        : getEntryScreenStatusBadge({ isRefunded, status: entry?.status, result: entry?.result });
    const adjustedMultiplier = getMultiplier(entry, 'adjusted');
    const initialMultiplier = getMultiplier(entry, multiplierType);
    const freeEntryApplied: boolean = isFreeEntry(entry);

    const gameModeTitle = getEntryTitle(entry?.gameMode);

    return (
        <Box gap={'s8'} marginBottom={'s24'}>
            <Box justifyContent={'center'} alignItems={'center'}>
                <Text variant={'headlineLarge'} fontWeight={700} numberOfLines={1}>
                    {picks?.length}-{t('bets:pick_lineup')}
                </Text>
                {freeEntryApplied ? null : isLoading ? (
                    <ActivityIndicator />
                ) : entry?.gameMode === GameMode.Dynamic ? (
                    <TouchableOpacity
                        hitSlop={20}
                        activeOpacity={0.8}
                        onPress={openPayoutSheet}
                        style={[common.row, common.justifyCenter, common.alignCenter]}
                    >
                        <Text variant={'bodyMedium'} color={ds.colorToken.text.brand}>
                            {getEntryMultiplier(entry)}
                        </Text>
                        <Box style={common.justifyCenter} height={20} paddingHorizontal={'s4'}>
                            <DownIcon color={ds.colorValue.icon.brand} />
                        </Box>
                    </TouchableOpacity>
                ) : (
                    <Text variant={'bodyMedium'} color={ds.colorToken.text.brand}>
                        {getEntryMultiplier(entry)}
                    </Text>
                )}
            </Box>
            <Row justifyContent={'space-around'} gap={'s12'} alignItems={'center'}>
                <Box alignItems={'center'} justifyContent={'center'} flex={1}>
                    <Text color={ds.colorToken.text.secondary} variant={'bodySmall'}>
                        {gameModeTitle}
                    </Text>
                    <Text
                        variant="headlineSmall"
                        fontWeight={700}
                        testID={`entry-amount-for-gameMode-${gameModeTitle}`}
                    >
                        {isLoading ? <ActivityIndicator /> : <>{toLocaleCurrency(entry?.amount ?? '')}</>}
                    </Text>
                </Box>
                <Box gap={'s4'} justifyContent={'center'} alignItems={'center'}>
                    <LineSeparator vertical style={styles.separator} />
                    <EntryStatusBadge status={entryStatus} showOpen />
                    <LineSeparator vertical style={styles.separator} />
                </Box>
                <Box alignItems={'center'} justifyContent={'center'} flex={1}>
                    <Text
                        color={isWinning ? ds.colorToken.text.success : ds.colorToken.text.secondary}
                        variant={'bodySmall'}
                    >
                        {getPayoutLabel(entry)}
                    </Text>
                    {isLoading ? (
                        <ActivityIndicator />
                    ) : (
                        <Text
                            variant={'headlineSmall'}
                            fontWeight={700}
                            color={isWinning ? ds.colorToken.text.success : ds.colorToken.text.primary}
                            testID={'entry-result-amount'}
                        >
                            {getEntryResultAmount(entry)}
                        </Text>
                    )}
                </Box>
            </Row>
            <EntryWarning
                entry={entry}
                initialMultiplier={initialMultiplier}
                newMultiplier={adjustedMultiplier}
                voidedPicksCount={voidedCount}
                hasRevivedPicks={hasRevivedPicks}
                hasRescuedPicks={hasRescuedPicks}
            />
        </Box>
    );
});

const styles = StyleSheet.create({
    separator: { opacity: 0.1, height: 18, marginTop: 2 },
});
